import wrapper from 'wrapper';
import ScrollStory from '@bbc/news-vj-scrollstory';
import { alwaysVisibleHeaderImages } from '../../shared_variables';
import { getOrientation, getDevice } from '../../utils/mobileOrDesktop';

const { assetsPath } = wrapper.vjConfigObject;

const imageCachebust = `${wrapper.vjConfigObject.version}.${Date.now()}`;

export default class ParallaxHeader {
    constructor(animated, supportedImageFormat) {
        const variant = wrapper.select('#variant-b') ? 'b' : 'a';

        // console.log('Optimizely variant', variant);
        if (variant !== 'a') {
            wrapper.select('.core-content-header').style.display = 'block';
            wrapper.select('.parallax-header').style.display = 'none';
        }

        this.animated = animated;
        this.supportedImageFormat = supportedImageFormat;

        this.headerWrapper = wrapper.select('.parallax-header');

        // Where the svg will be injected
        this.svgContainer = wrapper.select('.parallax-header .sticky-container .sticky-image');

        this.currentScreenType = ''; // 'portrait' or 'landscape'

        // Defines which SVG has been loaded
        this.loadedSVG = {
            landscape: false,
            portrait: false,
        };

        // Caches the SVG code so the browser doesn't have to re-fetch (less network requests)
        this.cachedSVGCode = {
            landscape: null,
            portrait: null,
        };

        // Keep loaded images in memory ({'image-id': 'image data string'})
        this.loadedImages = {};

        this.visibleHeaderImages = alwaysVisibleHeaderImages['portrait'];

        this.init();
    }

    init() {
        this.setScreenType();
        this.visibleHeaderImages = alwaysVisibleHeaderImages[this.currentScreenType];
        this.addListeners();
    }

    addListeners() {
        window.addEventListener('resize', () => {
            this.setScreenType();
        });
    }

    /**
     * Updates the SVG and its viewbox
     *
     * @param {string} screenType - 'portrait' or 'landscape'
     * @param {string} deviceType - 'mobile' or 'desktop'
     */
    update(screenType, deviceType) {
        if (!this.loadedSVG[screenType]) {
            this.loadSVG(screenType);
        }
        else {
            this.injectSVG(this.cachedSVGCode[screenType]);
        }
        this.currentScreenType = screenType;
        this.currentDeviceType = deviceType;
        this.updateViewbox();
    }

    /**
     * Assigns the screen type and determines whether an SVG should be fetched or injected from cache
     * (if previously loaded)
     */
    setScreenType() {
        const screenType = getOrientation();
        const screenChange = this.currentScreenType !== screenType;
        const deviceType = getDevice();
        const deviceChange = this.currentDeviceType !== deviceType;

        if (!screenChange && !deviceChange) return;
        this.loadSVG(screenType);
        this.applyTransition();

        this.update(screenType, deviceType);
    }

    /**
     * Fetches SVG from pre-defined path (default: assets/img/)
    */
    loadSVG(screenType) {
        const filePath = `${wrapper.path().projectAssets}/img/parallax-${screenType}.svg?cachebust=${imageCachebust}`;

        return fetch(filePath)
            .then((r) => r.text())
            .then((html) => {
                this.injectSVG(html);
                this.cacheSVG(screenType, html);
                this.loadedSVG[screenType] = true;
            })
            .catch(console.error.bind(console));
    }

    updateViewbox() {
        if (!this.svg) return;
        let viewbox = this.svg.getAttribute('viewBox');
        // viewbox = this.currentScreenType === 'landscape'
        //     ? '750 0 1440 1569'
        //     : '0 0 1125 3560';
        if (this.animated) {
            viewbox = this.currentScreenType === 'landscape'
                ? '750 0 1440 1569'
                : '0 0 1125 3060';
        }
        else {
            viewbox = this.currentScreenType === 'landscape'
                ? '750 0 1440 1569'
                : '0 0 1125 3260';
        }
        this.svg.setAttribute('viewBox', viewbox);
    }

    /**
     * Injects the SVG HTML code in the page within @property {HTMLElement} this.svgContainer
     *
     *
     * @param {string} html The HTML string that contains the fetched SVG
     */
    injectSVG(html) {
        this.svgContainer.innerHTML = `${html}`;
        this.svg = this.svgContainer.querySelector('svg');
        this.svg.setAttribute('role', 'img');
        this.svg.setAttribute('aria-label', __('banner_alt'));
        // target all g els that have id containing "layer-"
        this.layers = this.svg.querySelectorAll('g[id*=layer-]');
        // target all image els that have id containing "group-"
        this.images = Array.from(this.svg.querySelectorAll('image[id*=group-]'));
        // sort them properly
        this.images = this.images.sort((a, b) => {
            return a.id.localeCompare(b.id, undefined, {
                numeric: true,
                sensitivity: 'base',
            });
        });

        if (this.animated) {
            this.fadeInPerImage();
            this.hideAllVisibleImages();
            this.loadDirectlyVisibleImages();
            this.showHeader();
            this.initScrolly();
        }
        else {
            this.updateViewbox();
            this.showHeader();
            this.images.forEach(img => {
                this.loadImage(img);
                this.showImage(img);
                if (!img.dataset.loaded) {
                    this.loadImage(img);
                }
            });
        }
    }

    /**
     * Fades in each image that is not meant to be always visible, with a delay in between each fade-in.
     */
    fadeInPerImage() {
        for (let i = 0, delay = 0.5; i < this.images.length - 1; i++) {
            const el = this.images[i];
            const delayAmount = this.visibleHeaderImages.includes(el.id) ? 0.5 : 0; // seconds
            const duration = this.visibleHeaderImages.includes(el.id) ? 0.4 : 0.4; // seconds
            delay += delayAmount;
            const css = `
                transition: ${duration}s linear opacity;
                transition-delay: ${delay}s;
            `;

            if (this.visibleHeaderImages.includes(el.id)) {
                el.style = css;
            }
        }
    }

    /**
     * Saves the SVG code into an object per screen type
     *
     * @param {string} screenType - saves svg per screen type
     * @param {string} svgCode
     */
    cacheSVG(screenType, svgCode) {
        this.resetLayerParallax(); // don't keep transform data
        this.cachedSVGCode[screenType] = svgCode;
    }

    /**
     * When the page loads the header automaticaly calculates what images are in view to the user
     * and loads them in using @function this.isInViewport(image)
     *
     * {@link https://github.com/bbc/newsspec_34610-100-women-2023/blob/develop/source/app/shared_variables.json | shared_variables.js}
     */
    loadDirectlyVisibleImages() {
        this.images.forEach((image) => {
            this.loadImage(image);
            if (this.visibleHeaderImages.includes(image.id)) {
                this.showImage(image);
            }
        });
    }

    initScrolly() {
        this.images.forEach((img, i) => {
            img.classList.add('scene');
            img.classList.add(`scene${i}`);
        });
        this.layers.forEach(layer => {
            layer.initialTransformValue = layer.transform.baseVal[1] ? layer.transform.baseVal[1].matrix.e : 0;
        });
        new ScrollStory({
            guideline: this.currentScreenType === 'landscape' ? 90 : 70, // % from top, defaults to 50% if not given
            debug: false,
            container: this.headerWrapper,
            scenes: this.layers,
            progress: (scrollData) => {
                this.images.forEach(image => {
                    if (this.isInViewport(image)) {
                        if (!image.classList.contains('in-view')) this.showImage(image);
                    }
                });
                this.moveLayers(scrollData.totalProgress);
            },
            exit: () => {},
        });
    }

    /**
     * Checks if an image element is in view
     *
     *
     * @param {HTMLElement} image - the image element to be check if in view
     * @returns {boolean} - if the element is in view
     */
    isInViewport(image) {
        const rect = image.getBoundingClientRect();
        const margin = (window.innerHeight || document.documentElement.clientHeight) / 2;

        return (rect.top + margin) < (window.innerHeight || document.documentElement.clientHeight);
    }

    /**
     * Shows the header container
     */
    showHeader() {
        this.headerWrapper.classList.remove('hidden');
        this.headerWrapper.style.height = this.svg.height;
    }

    /**
     * Moves the <g id="layer-x"> elements of the SVG with different speeds to achieve a parallax effect
     *
     *
     * @property {HTMLElement} this.layers need to be a defined array (@see {@link injectSVG})
     * @param {number} scrollPosition - how far down the scrolly a user is.
     * This paremeter is optional and if not passed, the layers would reset position
     */
    moveLayers(scrollPosition = 0) {
        if (!this.animated) return;

        if (!this.scrollPosition) this.scrollPosition = scrollPosition;

        /**
         *  @todo Fine-tune the layer speed
         *  @todo Make the layer speed be set easily (by following a general calculation)
        */
        this.layers.forEach((layer, i) => {
            if (i === 0) return; // don't move layer-0
            const distance = i * 3;
            const value = `translateY(-${layer.initialTransformValue + ((scrollPosition - this.scrollPosition) * 50) * distance}px)`;
            layer.style.transform = value;
        });
    }

    /**
     * Fetch the relevant image from the assets
     *
     *
     * @param {HTMLElement} element - the SVG <image> element that needs to be loaded in
     */
    loadImage(element) {
        if (!element.dataset.loaded && !element.dataset.loading && !element.dataset.loadFailed) {
            const imageUrl = `${assetsPath}/img/header-img/${this.currentScreenType}/${element.id}-${this.currentDeviceType}.${this.supportedImageFormat}?v=${imageCachebust}`;

            element.dataset.loading = true;

            if (!this.loadedImages[imageUrl]) {
                this.fetchAndCache(imageUrl)
                    .then((dataUrl) => {
                        element.setAttribute('type', `image/${this.supportedImageFormat}`);
                        // element.setAttribute('href', dataUrl);
                        element.setAttribute('href', imageUrl);
                        element.dataset.loaded = true;
                        element.removeAttribute('data-loading');
                        this.loadedImages[imageUrl] = dataUrl;
                        this.cacheSVG(this.currentScreenType, this.svg.outerHTML);
                    },
                    () => {
                        element.removeAttribute('data-loaded');
                        element.removeAttribute('data-loading');
                        element.dataset.loadFailed = true;
                    });
            }
            else {
                element.setAttribute('href', this.loadedImages[imageUrl]);
                element.dataset.loaded = true;
                element.removeAttribute('data-loading');
            }
        }
    }

    /**
     * Fetches an image from the server, and outputs it into a data string.
     * This way the data string can be embedded into the SVG code
     * and when injected dynamically wouldn't need to re-fetch the image
     *
     *
     * @param {string} imageUrl - path to image on server
     * @returns
     */
    fetchAndCache(imageUrl) {
        return fetch(imageUrl)
            .then(response => ((response.status !== 404) ? response.blob() : null))
            .then(blob => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = (error) => reject(new Error(error));
                reader.readAsDataURL(blob);
            }));
    }

    hideAllVisibleImages() {
        if (this.svg) {
            this.svg.querySelectorAll('image.in-view').forEach(imageInView => imageInView.classList.remove('in-view'));
        }
    }

    /**
     * Remove layer transform to reset their position
     */
    resetLayerParallax() {
        this.layers.forEach(layer => layer.removeAttribute('style'));
    }

    /**
     * Make an image fade-in
     *
     *
     * @param {HTMLElement} element - the SVG <image> element that needs to fade-in
     */
    showImage(element) {
        if (!this.animated) {
            element.classList.add('in-view');
        }
        else if (element.dataset.loaded) {
            element.classList.add('in-view');
        }
        else {
            setTimeout(() => this.showImage(element), 0);
        }
    }

    /**
     * Trigger animation on logo
     *
     */
    applyTransition() {
        setTimeout(() => {
            wrapper.select('.logo-left').classList.add('logo-left_translate');
            // wrapper.select('.logo-left_arabic').classList.add('logo-left_translate_arabic');
            wrapper.select('.logo-right').classList.add('logo-right_translate');
            wrapper.select('.parallax_logo svg').classList.add('logo_fade');
        }, 0);
    }

    /**
     * Method to update animation status
     *
     * @param {boolean} [animated] - if the animation should be enabled or not
     * @memberof ParallaxHeader
     */
    updateAnimationStatus(animated) {
        this.animated = (typeof animated === 'boolean') ? animated : !this.animated;

        this.update(this.currentScreenType);
    }
}
